import { SxProps, styled, Theme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import CheckboxTree, { Node as RCTNode } from 'react-checkbox-tree';
import {
  SvgCheck2Bold,
  SvgLayoutNoneCustom,
  SvgSubtractSquareBold,
  SvgSubtractSquareCustom,
  SvgAddSquareCustom,
} from '@insights-ltd/design-library/components';

type TranslationKeys = Record<string, Record<string, Record<string, object>>>;

export type TreeViewProps = {
  keysObject: TranslationKeys;
  path: string;
  handleSelected: (value: string[]) => void;
  checkModel?: 'leaf' | 'all';
  sx?: SxProps<Theme>;
};

const StyledDiv = styled('div')(({ theme }) => ({
  '& > .react-checkbox-tree': {
    display: 'flex',
    flexDirection: 'row-reverse',
    fontSize: '16px',
  },
  '& > .react-checkbox-tree > ol': {
    flex: '1 1 auto',
  },
  '& > .react-checkbox-tree ol': {
    margin: 0,
    padding: 0,
    listStyleType: 'none',
  },
  '& > .react-checkbox-tree ol ol': {
    margin: 0,
    padding: 0,
    listStyleType: 'none',
    position: 'relative',
    top: '-2px',
    marginLeft: '20px',
  },
  '& > .react-checkbox-tree ol li': {
    margin: 0,
    padding: '0 7px',
    color: '#369',
    fontWeight: 'bold',
    lineHeight: '32px',
    borderLeft: `1px solid ${theme.palette.grey[500]}`,
  },
  '& > .react-checkbox-tree ol li.rct-node-leaf::before': {
    paddingLeft: '8px',
  },
  '& > .react-checkbox-tree ol li:only-of-type': {
    position: 'relative',
    top: '-3px',
  },
  '& > .react-checkbox-tree ol li:first-of-type': {
    position: 'relative',
    paddingTop: '4px',
  },
  '& > .react-checkbox-tree ol li:last-of-type': {
    position: 'relative',
    borderBottom: 'none',
    borderLeft: 'none',
  },
  '& > .react-checkbox-tree ol li::before': {
    position: 'relative',
    top: '-0.3em',
    display: 'inline-block',
    height: '1.3em',
    color: 'white',
    borderBottom: `1px solid ${theme.palette.grey[500]}`,
    content: '""',
  },
  '& > .react-checkbox-tree .rct-checkbox::before': {
    position: 'relative',
    top: '-13px',
    display: 'inline-block',
    width: '8px',
    height: '1.3em',
    color: 'white',
    borderBottom: `1px solid ${theme.palette.grey[500]}`,
    content: '""',
  },
  '& > .react-checkbox-tree ol li:first-of-type::before': {
    position: 'relative',
    left: '-8px',
    width: 0,
  },
  '& > .react-checkbox-tree ol li:last-of-type::before': {
    left: '-7px',
    borderLeft: `1px solid ${theme.palette.grey[500]}`,
  },
  '& > .react-checkbox-tree li .rct-node-parent > span .rct-text > label': {
    position: 'relative',
    left: '-1px',
  },
  '& > .react-checkbox-tree .PrivateSwitchBase-root-10': {
    paddingLeft: 0,
  },
  '& > .react-checkbox-tree button': {
    color: 'inherit',
    lineHeight: 'normal',
  },
  '& > .react-checkbox-tree button:focus': {
    outline: 'none',
  },
  '& > .react-checkbox-tree button:disabled': {
    cursor: 'not-allowed',
  },
  '& > .react-checkbox-tree .rct-text': {
    position: 'relative',
    left: '-16px',
  },
  '& > .react-checkbox-tree .rct-text > button': {
    position: 'relative',
    top: '4px',
    padding: 0,
  },
  '& > .react-checkbox-tree .rct-bare-label': {
    cursor: 'default',
  },
  '& > .react-checkbox-tree label': {
    marginBottom: 0,
    color: 'black',
    cursor: 'pointer',
  },
  '& > .react-checkbox-tree :not(.rct-native-display) input': {
    display: 'none',
  },
  '& > .react-checkbox-tree .rct-native-display input': {
    margin: '0 5px',
  },
  '& > .react-checkbox-tree .rct-icon': {
    display: 'inline-block',
    fontWeight: 'normal',
    fontFamily: 'Font Awesome 5 Free, FontAwesome, sans-serif',
    fontStyle: 'normal',
    fontVariant: 'normal',
    textAlign: 'center',
    textRendering: 'auto',
  },
  '& > .react-checkbox-tree .rct-disabled > .rct-text > label': {
    cursor: 'not-allowed',
    opacity: 0.75,
  },
  '&> .react-checkbox-tree .rct-disabled > .rct-text > label:hover': {
    background: 'transparent',
  },
  '& > .react-checkbox-tree .rct-disabled > .rct-text > label:active': {
    background: 'transparent',
  },
  '& > .react-checkbox-tree .rct-options': {
    flex: '0 0 auto',
    marginLeft: '0.5rem',
    textAlign: 'right',
  },
  '& > .react-checkbox-tree .rct-option': {
    padding: '0 4px',
    fontSize: '18px',
    background: 'none',
    border: 0,
    cursor: 'pointer',
    opacity: 0.75,
  },
  '& > .react-checkbox-tree .rct-option:hover': {
    opacity: 1,
  },
  '& > .react-checkbox-tree .rct-option + .rct-option': {
    marginLeft: '2px',
  },
  '& > .react-checkbox-tree .rct-collapse': {
    color: 'inherit',
    fontSize: '12px',
    lineHeight: 'normal',
    background: 'none',
    border: 0,
    position: 'relative',
    top: '8px',
  },
  '& > .react-checkbox-tree .rct-collapse > *': {
    display: 'inline-block',
    margin: 0,
  },
  '& > .react-checkbox-tree .rct-collapse .rct-collapse-btn': {
    cursor: 'pointer',
  },
  '& .rct-collapse > .rct-icon-expand-close': {
    opacity: 0.5,
  },
  '& > .react-checkbox-tree .rct-collapse > .rct-icon-expand-close:hover': {
    opacity: 1,
  },
  '& > .react-checkbox-tree .rct-checkbox': {
    position: 'relative',
    top: '8px',
  },
  '& > .react-checkbox-tree .rct-checkbox > *': {
    display: 'inline-block',
    margin: 0,
  },
  '& > .react-checkbox-tree .rct-node-icon': {
    position: 'relative',
    top: '8px',
    color: '#33c',
  },
  '& > .react-checkbox-tree .rct-node-icon > *': {
    display: 'inline-block',
    margin: 0,
  },
  '& > .react-checkbox-tree .rct-checkbox .rct-native-display': {
    display: 'none',
  },
  '& > .react-checkbox-tree .rct-node-clickable': {
    cursor: 'pointer',
  },
  '& > .react-checkbox-tree .rct-node label:hover': {
    background: 'rgba(51, 51, 204, 0.1)',
  },
  '& > .react-checkbox-tree .rct-node label:focus': {
    background: 'rgba(51, 51, 204, 0.2)',
    outline: 0,
  },
  '& > .react-checkbox-tree .rct-title': {
    padding: '0 12px',
  },
  '& > .rct-icons-fa4 .rct-icon-expand-close::before': {
    content: '"\f054"',
  },
  '& > .rct-icons-fa4 .rct-icon-expand-open::before': {
    content: '"\f078"',
  },
  '& > .rct-icons-fa4 .rct-icon-uncheck::before': {
    content: '"\f096"',
  },
  '& > .rct-icons-fa4 .rct-icon-check::before': {
    content: '"\f046"',
  },
  '& > .rct-icons-fa4 .rct-icon-half-check::before': {
    opacity: 0.5,
    content: '"\f046"',
  },
  '& > .rct-icons-fa4 .rct-icon-leaf::before': {
    content: '"\f016"',
  },
  '& > .rct-icons-fa4 .rct-icon-parent-open::before': {
    content: '"\f115"',
  },
  '& > .rct-icons-fa4 .rct-icon-parent-close::before': {
    content: '"\f114"',
  },
  '& > .rct-icons-fa4 .rct-icon-expand-all::before': {
    content: '"\f0fe"',
  },
  '& > .rct-icons-fa4 .rct-icon-collapse-all::before': {
    content: '"\f146"',
  },
  '& > .rct-icons-fa5 .rct-icon-expand-close::before': {
    fontWeight: 900,
    content: '"\f054"',
  },
  '& > .rct-icons-fa5 .rct-icon-expand-open::before': {
    fontWeight: 900,
    content: '"\f078"',
  },
  '& > .rct-icons-fa5 .rct-icon-uncheck::before': {
    content: '"\f0c8"',
  },
  '& > .rct-icons-fa5 .rct-icon-check::before': {
    content: '"\f14a"',
  },
  '& > .rct-icons-fa5 .rct-icon-half-check::before': {
    opacity: 0.5,
    content: '"\f14a"',
  },
  '& > .rct-icons-fa5 .rct-icon-leaf::before': {
    content: '"\f15b"',
  },
  '& > .rct-icons-fa5 .rct-icon-parent-open::before': {
    content: '"\f07c"',
  },
  '& > .rct-icons-fa5 .rct-icon-parent-close::before ': {
    content: '"\f07b"',
  },
  '& > .rct-icons-fa5 .rct-icon-expand-all::before': {
    content: '"\f0fe"',
  },
  '& > .rct-icons-fa5 .rct-icon-collapse-all::before': {
    content: '"\f146"',
  },
  '& .rct-direction-rtl': {
    direction: 'rtl',
  },
  '& .rct-direction-rtl ol ol': {
    paddingRight: '24px',
    paddingLeft: 0,
  },
  '& .rct-direction-rtl > .rct-icons-fa4 .rct-icon-expand-close::before': {
    content: '"\f105"',
  },
  '& .rct-direction-rtl .rct-icons-fa5 .rct-icon-expand-close::before': {
    content: '"\f053"',
  },
}));

const StyledCheckBoxChecked = styled(SvgCheck2Bold)(({ theme }) => ({
  fill: theme.palette.blue.main,
}));

const StyledCheckBoxUnchecked = styled(SvgLayoutNoneCustom)(({ theme }) => ({
  stroke: theme.palette.blue.main,
  backgroundColor: theme.palette.grey[100],
}));

const StyledCheckBoxPartial = styled(SvgSubtractSquareBold)(({ theme }) => ({
  fill: theme.palette.blue.main,
}));

const StyledExpand = styled(SvgAddSquareCustom)(({ theme }) => ({
  height: '17',
  width: '17',
  fill: theme.palette.grey[100],
}));

const StyledCollapse = styled(SvgSubtractSquareCustom)(({ theme }) => ({
  height: '17',
  width: '17',
  fill: theme.palette.grey[100],
}));

const convertData = (keysObject: TranslationKeys, path: string): RCTNode[] =>
  Object.keys(keysObject).map((item) => {
    const newPath = `${path}.${item}`;
    const newTree = keysObject[item] as TranslationKeys;
    const node: RCTNode = Object.keys(newTree).length
      ? { value: newPath, label: item, children: convertData(newTree, newPath) }
      : { value: newPath, label: item };
    return node;
  });

const TreeView = ({
  keysObject,
  path,
  handleSelected,
  checkModel = 'all',
  sx,
  ...props
}: TreeViewProps) => {
  const [checked, setChecked] = useState<string[]>([]);
  const [expanded, setExpanded] = useState<string[]>([]);
  const nodes = convertData(keysObject, path);

  useEffect(() => {
    const results: string[] = [];
    let newList = checked;
    while (newList.length) {
      const key = newList[0];
      newList = newList.filter((row) => !row.match(key));
      results.push(key);
    }
    handleSelected(results);
  }, [handleSelected, checked]);

  return (
    <StyledDiv sx={sx}>
      <CheckboxTree
        {...props}
        nodes={nodes}
        checked={checked}
        expanded={expanded}
        onCheck={setChecked}
        onExpand={setExpanded}
        nativeCheckboxes={false}
        checkModel={checkModel}
        icons={{
          check: <StyledCheckBoxChecked />,
          uncheck: <StyledCheckBoxUnchecked />,
          halfCheck: <StyledCheckBoxPartial />,
          expandClose: <StyledExpand />,
          expandOpen: <StyledCollapse />,
          expandAll: <h1>Expand All</h1>,
          collapseAll: <h1>Collapse All</h1>,
          parentClose: null,
          parentOpen: null,
          leaf: null,
        }}
      />
    </StyledDiv>
  );
};

export default TreeView;
